/**
 * Deity-side card content (the +4 pole), keyed by tool id. Mirrors the MoyRi
 * artifact exactly: the ability line, the "WHEN YOU HAVE IT" description, and
 * the three STEP-tier difficulty labels that feed the ACQUISITION ladder.
 * Malady-side content lives in swap-data.ts.
 */

/**
 * A Principle: a complete "new thing you now do" sentence + how many times a
 * week. Every Principle must pass the four-test gate:
 *  1. COUNTABLE — completing it produces a persisted event the app can see.
 *  2. SELF-INITIATED — on the user's own schedule, never gated on an external
 *     event ("when X happens" is banned).
 *  3. ALIGNED — actually trains the deity's ability, not busywork.
 *  4. MANUAL — the user produces the judgment by hand, not the tool's
 *     automation doing it for them.
 * Ordered easy→hard: first = in-app tool proficiency, later = deeper practice.
 * 10 per deity; the user picks some to run each season. Securing your picks to
 * a season acquires the deity.
 */
export interface PrincipleDef {
  /** Short punchy dare — the thing you say to yourself. ≤6 words. */
  title: string;
  /** One plain line: exactly what to do, so the action is unmistakable. */
  detail: string;
  perWeek: number;
}

export interface DeityInfo {
  ability: string;
  have: string;
  /** Light / Standard / Heavy labels for the STEP tier. */
  steps: [string, string, string];
  /** Level 1 principles (basic acquisition) — the pick list until you hit L2. */
  principles: PrincipleDef[];
  /** Level 2 principles — advanced usage, populated once L1 is banked. */
  principlesL2?: PrincipleDef[];
}

/** A principle with its stable id (`toolId:levelIdx:principleIdx`). Picks and
 *  the completion log key by this id, so changing the level's list never
 *  collides with banked history. */
export type PrincipleWithId = PrincipleDef & { id: string };

export const DEITY_DATA: Record<string, DeityInfo> = {
  telos: {
    ability: 'the ability to set a condition-state (STAR) aim and steer by it.',
    have: 'You always know which way is ‘forward.’ Choices get easy — each is measured against a fixed star. The map can change; your heading doesn’t.',
    steps: ['name today’s aim', 'write the week’s aim', 'full STAR endeavour'],
    principles: [
      { title: 'Mark the day', detail: 'Log did-work or missed on one domain', perWeek: 7 },
      { title: 'Nothing unmarked', detail: 'Mark all 5 domains before bed', perWeek: 6 },
      { title: 'One honest line', detail: 'In a domain’s notes, write how today served its vision', perWeek: 5 },
      { title: 'Face the slip', detail: 'Note which domain drifted today, and why', perWeek: 4 },
      { title: 'Read your north', detail: 'Open a domain’s vision, then note today’s one move toward it', perWeek: 4 },
      { title: 'Swap a habit', detail: 'Re-pick one domain’s habit chip to fit who you are now', perWeek: 3 },
      { title: 'Set the action', detail: 'Add or change one domain’s task action', perWeek: 3 },
      { title: 'Sharpen a principle', detail: 'Rewrite one domain’s principle line', perWeek: 2 },
      { title: 'Rebuild a cascade', detail: 'Redo one domain’s vision → habits → actions, top to bottom', perWeek: 2 },
      { title: 'Re-aim all five', detail: 'Walk every domain, re-pick the week’s habits and principles', perWeek: 1 },
    ],
  },
  numa: {
    ability: 'the ability to match your load to the energy you actually have.',
    have: 'You spend to your real reserves, not your guilt. You push hard and stop clean — no crash. Rest stops feeling like cheating.',
    steps: ['one energy check-in', 'set today’s load', 'hold the 80/80/80 gate'],
    principles: [
      { title: 'Match the day', detail: 'Reschedule one river task to fit your real energy', perWeek: 7 },
      { title: 'Clear one clean', detail: 'Mark one river task done and mean it', perWeek: 6 },
      { title: 'Rescue the overdue', detail: 'Drag one overdue task to a day you’ll actually do it', perWeek: 5 },
      { title: 'Read the flow', detail: 'Check your flow score, then move one task off your worst day', perWeek: 4 },
      { title: 'Cut to three', detail: 'Trim today down to the 3 tasks you’ll really finish', perWeek: 3 },
      { title: 'Push the nice-to-have', detail: 'Reschedule one non-urgent task to next week', perWeek: 3 },
      { title: 'Time the dodge', detail: 'Put a real time on the task you keep avoiding', perWeek: 2 },
      { title: 'Kill a phantom', detail: 'Table one task you’re never actually doing', perWeek: 2 },
      { title: 'Find the peak', detail: 'Read the month charts, move 3 tasks off your heaviest day', perWeek: 2 },
      { title: 'Rebalance the month', detail: 'Reschedule next week’s load from what the charts show', perWeek: 1 },
    ],
  },
  moyri: {
    ability: 'the ability to engineer a skill-system that produces an outcome.',
    have: 'Outcomes stop depending on willpower — you build the machine once and it runs. You trust the system, not the mood.',
    steps: ['pick one system', 'engineer the month', 'full repeatable system'],
    principles: [
      { title: 'Log the habit', detail: 'Mark one habit a win or a loss today', perWeek: 7 },
      { title: 'Rate it real', detail: 'Score one habit 1–10 and write the why in its summary', perWeek: 5 },
      { title: 'Catch the trigger', detail: 'Add one trigger you noticed to a habit’s list', perWeek: 4 },
      { title: 'Name the tell', detail: 'Log an absence-sign that shows it’s creeping back', perWeek: 4 },
      { title: 'Arm a defense', detail: 'Add one prevention action to a habit', perWeek: 3 },
      { title: 'Write the dissolve', detail: 'Add one dissolve-action for when the habit fires', perWeek: 3 },
      { title: 'Count the cost', detail: 'Fill in what this habit actually costs you', perWeek: 2 },
      { title: 'Trace the root', detail: 'Write how you got here on one habit', perWeek: 2 },
      { title: 'Full audit', detail: 'Rewrite one habit’s whole profile — symptoms to costs', perWeek: 1 },
      { title: 'Build one clean', detail: 'Create a new habit from scratch, every field filled', perWeek: 1 },
    ],
  },
  oracle: {
    ability: 'the skill of taking honest counsel — reading where you truly stand and letting intake questions prescribe the work.',
    have: 'You see your position clearly before you act. No guessing, no flying blind — the intake tells you exactly what to work on next.',
    steps: ['run one quick intake', 'run a full weekly intake', 'run intake across every gear'],
    principles: [
      { title: 'Rings first', detail: 'Read all three rings before your first task', perWeek: 7 },
      { title: 'Hit the weak spot', detail: 'Tap the lowest ring and clear one item there', perWeek: 5 },
      { title: 'Take the counsel', detail: 'Capture what the rings say to fix into HRMEZ', perWeek: 4 },
      { title: 'Close the gap', detail: 'Pick the weakest ring, finish one thing in its tool', perWeek: 4 },
      { title: 'Tasks ring up', detail: 'Clear one scheduled task so the Tasks ring climbs', perWeek: 3 },
      { title: 'Projects ring up', detail: 'Fill one missing field so a project counts complete', perWeek: 3 },
      { title: 'Filing ring up', detail: 'Route one pending item so the Filing ring climbs', perWeek: 3 },
      { title: 'Read the trend', detail: 'Capture whether each ring rose or fell since last look', perWeek: 2 },
      { title: 'All green', detail: 'Get every ring over the line in one sitting', perWeek: 1 },
      { title: 'Weekly counsel', detail: 'Schedule the week’s 3 fixes in KYRS from the rings', perWeek: 1 },
    ],
  },
  astra: {
    ability: 'the skill of holding all five life sectors in one view and steering by the whole compass.',
    have: 'No sector drifts unseen. You always know where the whole board stands, not just whatever’s loudest today.',
    steps: ['check one sector', 'run a full compass review', 'steer every sector from one view'],
    principles: [
      { title: 'One project honest', detail: 'Update one project’s status to today’s truth', perWeek: 6 },
      { title: 'Feed the starving', detail: 'Add a KYRS task for your most-ignored sector', perWeek: 4 },
      { title: 'Name the neglect', detail: 'Capture which sector you’ve been dodging into HRMEZ', perWeek: 4 },
      { title: 'Read the board', detail: 'Scan every chart, then update one project you find stale', perWeek: 3 },
      { title: 'Sector sweep', detail: 'Update every project in one sector', perWeek: 3 },
      { title: 'Close a project', detail: 'Mark one finished project done', perWeek: 2 },
      { title: 'Balance check', detail: 'Capture your hottest and coldest sector into HRMEZ', perWeek: 2 },
      { title: 'Whole board', detail: 'Update every project’s status in one pass', perWeek: 1 },
      { title: 'Read the verdict', detail: 'Capture what the whole board is telling you', perWeek: 1 },
      { title: 'Steer the week', detail: 'Set the week’s focus sector and add its first task', perWeek: 1 },
    ],
  },
  hrmez: {
    ability: 'the ability to hold steady signal through noise.',
    have: 'The noise stops running you. You can sit in chaos and still hear the one thing that matters.',
    steps: ['one grounding pass', 'filter today’s noise', 'full signal protocol'],
    principles: [
      { title: 'Catch it', detail: 'Capture one stray thought the moment it hits', perWeek: 7 },
      { title: 'File it yourself', detail: 'Route a capture by hand — don’t let the AI pick', perWeek: 6 },
      { title: 'Title the noise', detail: 'Give one capture a clean first-line title', perWeek: 4 },
      { title: 'Sort the pile', detail: 'Send three captures to their right homes', perWeek: 4 },
      { title: 'Empty the pad', detail: 'Route or dismiss everything sitting in HRMEZ', perWeek: 3 },
      { title: 'Read the memory', detail: 'Check the routing log and flag one bad call', perWeek: 2 },
      { title: 'Fix a misfile', detail: 'Correct one thing the AI routed wrong', perWeek: 2 },
      { title: 'Teach a rule', detail: 'Add one routing rule so it files itself next time', perWeek: 2 },
      { title: 'Inbox zero', detail: 'Clear every pending capture in one sitting', perWeek: 1 },
      { title: 'Tune the machine', detail: 'Review the memory log, fix or add one rule', perWeek: 1 },
    ],
  },
  kyrs: {
    ability: 'the ability to schedule by priority, not by whatever shouts loudest.',
    have: 'Your day runs on what matters, not what’s urgent. You calmly pick one thing and let the rest wait.',
    steps: ['order 1 priority', 'schedule the week', 'priority-sequence all'],
    principles: [
      { title: 'Nothing unranked', detail: 'Set the value on every task before you save it', perWeek: 7 },
      { title: 'My own order', detail: 'Drag today’s list into the order you’ll work it', perWeek: 5 },
      { title: 'Pick the one', detail: 'Move your true #1 to the top by hand', perWeek: 5 },
      { title: 'Table a pretender', detail: 'Send one fake-urgent task to Tabled', perWeek: 4 },
      { title: 'Time the top three', detail: 'Put real times on today’s top 3', perWeek: 3 },
      { title: 'Estimate honest', detail: 'Set an ETC on one task you keep underestimating', perWeek: 3 },
      { title: 'Clear the noise', detail: 'Table everything that doesn’t deserve this week', perWeek: 2 },
      { title: 'Work the bell', detail: 'Review and keep-or-move every auto-scheduled task', perWeek: 2 },
      { title: 'Empty the urgent', detail: 'Finish and check off your top 3 before noon', perWeek: 1 },
      { title: 'Re-rank the backlog', detail: 'Re-score values and ETC on your whole main list', perWeek: 1 },
    ],
  },
  krnos: {
    ability: 'the ability to build a realistic week and reschedule when reality changes.',
    have: 'Your week has a shape you can adjust — plans bend instead of breaking.',
    steps: ['rough the week', 'build the week', 're-plan on change'],
    principles: [
      { title: 'Build tomorrow', detail: 'Lay out tomorrow’s blocks before tonight ends', perWeek: 6 },
      { title: 'Block the one', detail: 'Put your most important task on the calendar first', perWeek: 5 },
      { title: 'Rehome the missed', detail: 'Move every block you missed to a real new slot', perWeek: 4 },
      { title: 'Guard the morning', detail: 'Reserve one focus block nobody can take', perWeek: 3 },
      { title: 'Colour the day', detail: 'Colour-sort tomorrow’s events so you can read it', perWeek: 3 },
      { title: 'Kill a ghost', detail: 'Delete one event that’s not really happening', perWeek: 3 },
      { title: 'Set the recurring', detail: 'Turn one repeating commitment into a real series', perWeek: 2 },
      { title: 'Build the week', detail: 'Give every known commitment a day and a time', perWeek: 1 },
      { title: 'Read what happened', detail: 'Look at planned vs lived, fix one drift', perWeek: 1 },
      { title: 'Rebuild the month', detail: 'Reschedule what’s left of the month to reality', perWeek: 1 },
    ],
  },
  janus: {
    ability: 'the ability to make criteria-based, informed decisions.',
    have: 'Decisions close and stay closed — you trust your own call and stop re-litigating.',
    steps: ['set the criteria', 'decide one thing', 'close the loop'],
    principles: [
      { title: 'Name the fork', detail: 'Open one real decision and give it a title', perWeek: 4 },
      { title: 'Grid it', detail: 'Place a decision on the grid, write the verdict in notes', perWeek: 3 },
      { title: 'Set the criteria', detail: 'List what actually matters for one decision', perWeek: 3 },
      { title: 'Weight them', detail: 'Rank the criteria by how much they count', perWeek: 3 },
      { title: 'Score the options', detail: 'Run one decision through the matrix by hand', perWeek: 2 },
      { title: 'Crown the winner', detail: 'Record which option won, and why, in the notes', perWeek: 2 },
      { title: 'Log the exposure', detail: 'Write one risk you’re accepting on a decision', perWeek: 2 },
      { title: 'Close it', detail: 'Mark one situation Decided — and don’t reopen it', perWeek: 2 },
      { title: 'Trust the call', detail: 'Close a decision without re-litigating it', perWeek: 1 },
      { title: 'Full pass', detail: 'Take one decision title-to-close in a single sitting', perWeek: 1 },
    ],
  },
  tkne: {
    ability: 'the ability to turn intent into a concrete sequence.',
    have: 'Vague goals become steps you can actually take. You always know the next move.',
    steps: ['name next 1 step', 'plan 3 steps', 'full path'],
    principles: [
      { title: 'Next step named', detail: 'Write the next step on your active project first thing', perWeek: 6 },
      { title: 'Status honest', detail: 'Update every step to P, S, or C', perWeek: 4 },
      { title: 'Log the session', detail: 'Write one guide entry: goal, deliverable, result', perWeek: 3 },
      { title: 'Define done', detail: 'Write one clear definition-of-done item', perWeek: 3 },
      { title: 'Cut the MVP', detail: 'Mark what’s core vs nice-to-have on one project', perWeek: 3 },
      { title: 'Name a risk', detail: 'Log one risk or constraint on the design', perWeek: 2 },
      { title: 'Forecast a step', detail: 'Add one forecast step with a real date', perWeek: 2 },
      { title: 'Set an objective', detail: 'Write one objective with its proof', perWeek: 2 },
      { title: 'Sync the plan', detail: 'Push your Directions steps into KYRS tasks', perWeek: 1 },
      { title: 'Full design pass', detail: 'Take one project problem-to-forecast in one sitting', perWeek: 1 },
    ],
  },
  themis: {
    ability: 'the ability to break any action into an ordered protocol.',
    have: 'Big vague actions stop scaring you — you can always name step 1, so you act instead of freeze.',
    steps: ['1 protocol step', '3-step protocol', 'full 5-step protocol'],
    principles: [
      { title: 'Run it clean', detail: 'Run one protocol start to finish on the timer', perWeek: 6 },
      { title: 'No winging it', detail: 'Don’t start your hard task without its protocol', perWeek: 4 },
      { title: 'Time a step', detail: 'Set a real duration on one protocol step', perWeek: 4 },
      { title: 'Protocol the feel', detail: 'Turn one wing-it task into a written 3-step', perWeek: 3 },
      { title: 'Name step one', detail: 'For a task that scares you, just define step 1', perWeek: 3 },
      { title: 'Read the log', detail: 'Check your completion log, flag one weak protocol', perWeek: 3 },
      { title: 'Tighten one', detail: 'Trim a protocol’s steps or timings from what you learned', perWeek: 2 },
      { title: 'Finish, don’t bail', detail: 'Complete one protocol without abandoning it', perWeek: 2 },
      { title: 'Build the five', detail: 'Break your hardest task into a full 5-step protocol', perWeek: 1 },
      { title: 'Stack a routine', detail: 'Chain protocols into one morning run', perWeek: 1 },
    ],
  },
  metis: {
    ability: 'the ability to lay a day into sequenced slots.',
    have: 'Your day has a shape before it starts. Time stops leaking — each hour has a job.',
    steps: ['slot 3 tasks', 'slot the whole day', 'time-box fully'],
    principles: [
      { title: 'Day built early', detail: 'Lay out today’s agenda before it starts', perWeek: 7 },
      { title: 'Every task a time', detail: 'Give every agenda item a real time', perWeek: 6 },
      { title: 'Close it honest', detail: 'Mark every item Done or Missed, with a reason', perWeek: 5 },
      { title: 'Face the miss', detail: 'Log why one task didn’t happen', perWeek: 4 },
      { title: 'Run one boxed', detail: 'Run one block on its timer, then close it', perWeek: 3 },
      { title: 'Deep work first', detail: 'Time-box your hardest task at the top of the day', perWeek: 3 },
      { title: 'Reschedule the slip', detail: 'Move one missed item to a real new slot', perWeek: 3 },
      { title: 'Nail the scratch', detail: 'Timer one quick task instead of letting it drift', perWeek: 2 },
      { title: 'Link the week', detail: 'Tie today’s key items to a weekly goal', perWeek: 2 },
      { title: 'Design the session', detail: 'Fill one task’s O.R.D.E.R sheet before you start', perWeek: 1 },
    ],
  },
  clio: {
    ability: 'the ability to close a loop and carry the lesson forward.',
    have: 'Nothing’s wasted — every day gets read and folded into the next. You improve instead of repeat.',
    steps: ['one-line log', 'daily revise', 'weekly re-project'],
    principles: [
      { title: 'The day gets written', detail: 'Answer every prompt in today’s entry', perWeek: 6 },
      { title: 'One line minimum', detail: 'Never let a day go completely unlogged', perWeek: 5 },
      { title: 'Mark the domains', detail: 'Mark your TELOS domains inside the entry', perWeek: 5 },
      { title: 'Close the itinerary', detail: 'Mark every item Done, Started, or Rescheduled', perWeek: 4 },
      { title: 'Log the reason', detail: 'When you reschedule, write down why', perWeek: 3 },
      { title: 'Carry the lesson', detail: 'Read yesterday, write its one lesson into today', perWeek: 3 },
      { title: 'Name the win', detail: 'Log one thing that actually worked today', perWeek: 3 },
      { title: 'Face the pattern', detail: 'Write the thing that keeps repeating', perWeek: 2 },
      { title: 'Steal from the episode', detail: 'Generate the week’s EPIC, write its one lesson', perWeek: 1 },
      { title: 'Weekly re-project', detail: 'Read the week back, set next week’s aim', perWeek: 1 },
    ],
  },
  argos: {
    ability: 'the ability to watch the whole field and catch drift early.',
    have: 'You see the slip before it’s a crisis. Small corrections, never big rescues.',
    steps: ['one status scan', 'weekly review', 'full field audit'],
    principles: [
      { title: 'Name yesterday’s thief', detail: 'Capture your top time-sink into HRMEZ', perWeek: 6 },
      { title: 'Face the number', detail: 'Capture yesterday’s total screen time into HRMEZ', perWeek: 5 },
      { title: 'Catch the drift', detail: 'Spot one slip and capture the correction', perWeek: 3 },
      { title: 'Week’s biggest thief', detail: 'Capture the week’s worst time-sink into HRMEZ', perWeek: 3 },
      { title: 'Guard the hour', detail: 'Find your worst hour, capture a plan to protect it', perWeek: 2 },
      { title: 'Shop check', detail: 'Capture one impulse buy the tracker caught', perWeek: 2 },
      { title: 'Did the block hold?', detail: 'Capture whether your focus blocks actually held', perWeek: 2 },
      { title: 'Read the pattern', detail: 'Generate the AI pattern read, capture one to fix', perWeek: 1 },
      { title: 'Month audit', detail: 'Read the month view, capture one correction', perWeek: 1 },
      { title: 'Set the guardrail', detail: 'Turn one pattern into a rule for next week', perWeek: 1 },
    ],
  },
  nemos: {
    ability: 'the ability to file and retrieve without loss.',
    have: 'Everything has a home and comes back fast — nothing drops.',
    steps: ['file one item', 'clear the inbox', 'full weekly reset'],
    principles: [
      { title: 'One gets a home', detail: 'File one loose note, file, or photo into its folder', perWeek: 6 },
      { title: 'Catch the stray', detail: 'File that one thing floating in your inbox', perWeek: 5 },
      { title: 'Name it right', detail: 'Give one saved thing a findable name', perWeek: 4 },
      { title: 'Empty the review', detail: 'Accept or Override every pending filing decision', perWeek: 4 },
      { title: 'Tidy a folder', detail: 'Rename, merge, or clear out one folder', perWeek: 3 },
      { title: 'Kill the dupe', detail: 'Delete one thing you’ve saved twice', perWeek: 3 },
      { title: 'Route the record', detail: 'Clear one pending file classification', perWeek: 2 },
      { title: 'Set a recall', detail: 'Make one thing resurface when you’ll need it', perWeek: 2 },
      { title: 'Folder sweep', detail: 'Reorganize one section end to end', perWeek: 1 },
      { title: 'Every inbox zero', detail: 'Notes filed, files placed, review queue empty', perWeek: 1 },
    ],
  },
};

const FALLBACK: DeityInfo = {
  ability: 'the ability this deity trains.',
  have: 'When you have it, this pattern runs on its own.',
  steps: ['a light rep', 'a standard rep', 'a heavy rep'],
  principles: [
    { title: 'Light rep', detail: 'Do one light rep in the tool', perWeek: 5 },
    { title: 'Standard rep', detail: 'Do one standard rep in the tool', perWeek: 3 },
    { title: 'Heavy rep', detail: 'Do one heavy rep in the tool', perWeek: 2 },
    { title: 'Full run', detail: 'Run the full practice start to finish', perWeek: 1 },
  ],
};

export function getDeity(toolId: string): DeityInfo {
  return DEITY_DATA[toolId] ?? FALLBACK;
}

/** The principle levels for a deity, lowest first (L1, [L2, …]). */
function deityLevels(toolId: string): PrincipleDef[][] {
  const info = getDeity(toolId);
  const levels: PrincipleDef[][] = [info.principles];
  if (info.principlesL2 && info.principlesL2.length) levels.push(info.principlesL2);
  return levels;
}

/** Stable id for a principle: `toolId:levelIdx:principleIdx`. */
export function principleId(toolId: string, levelIdx: number, principleIdx: number): string {
  return `${toolId}:${levelIdx}:${principleIdx}`;
}

/** The pickable principles (with ids) for a 1-based level, clamped to what
 *  exists — higher levels fall back to the highest authored set. */
export function principlesForLevel(toolId: string, level: number): PrincipleWithId[] {
  const levels = deityLevels(toolId);
  const li = Math.min(Math.max(0, level - 1), levels.length - 1);
  return levels[li].map((p, pi) => ({ ...p, id: principleId(toolId, li, pi) }));
}

/** Everything the engine needs: per-level id lists + perWeek by id (all levels). */
export function deityLevelData(toolId: string): { levelIds: string[][]; perWeek: Record<string, number> } {
  const levels = deityLevels(toolId);
  const levelIds: string[][] = [];
  const perWeek: Record<string, number> = {};
  levels.forEach((lvl, li) => {
    const ids: string[] = [];
    lvl.forEach((p, pi) => {
      const id = principleId(toolId, li, pi);
      ids.push(id);
      perWeek[id] = p.perWeek;
    });
    levelIds.push(ids);
  });
  return { levelIds, perWeek };
}

/** L1 principle id for a legacy numeric index (used to migrate old picks/log). */
export function legacyL1Id(toolId: string, idx: number): string {
  return principleId(toolId, 0, idx);
}

/** The four ACQUISITION tiers. STEP is per-deity (its `steps`); the rest are shared. */
export const TIER_LABELS = ['STEP · the moment', 'TODAY · the day', 'AIM · the week', 'GOAL · the month'];

/**
 * A single commitment the user can pick for a deity: one task + how many times
 * a week. Difficulty is baked into the (task × frequency) combination — a light
 * task run often vs. a heavy task run rarely — so there is no separate
 * easy/mid/hard label, and no week/month tier to choose. The user picks exactly
 * one of these; it becomes a recurring KYRS task.
 */
export interface StepOption {
  task: string;
  perWeek: number;
}

/** 7 pick-one options per deity, from a light task done often to a heavy task
 *  done rarely. `steps` = [light, standard, heavy] from the deity's data. */
export function stepOptions(steps: [string, string, string]): StepOption[] {
  const [light, standard, heavy] = steps;
  return [
    { task: light, perWeek: 7 },
    { task: light, perWeek: 5 },
    { task: standard, perWeek: 4 },
    { task: standard, perWeek: 3 },
    { task: standard, perWeek: 2 },
    { task: heavy, perWeek: 2 },
    { task: heavy, perWeek: 1 },
  ];
}

export interface TierOpt {
  label: string;
  diff: 'EASY' | 'MID' | 'HARD';
}

export function acquisitionTiers(steps: [string, string, string]): { tier: string; opts: TierOpt[] }[] {
  return [
    {
      tier: TIER_LABELS[0],
      opts: [
        { label: `Light — ${steps[0]}`, diff: 'EASY' },
        { label: `Standard — ${steps[1]}`, diff: 'MID' },
        { label: `Heavy — ${steps[2]}`, diff: 'HARD' },
      ],
    },
    {
      tier: TIER_LABELS[1],
      opts: [
        { label: 'Any 3 days', diff: 'EASY' },
        { label: '4 of 5 weekdays', diff: 'MID' },
        { label: 'Every weekday', diff: 'HARD' },
      ],
    },
    {
      tier: TIER_LABELS[2],
      opts: [
        { label: '1 ordered week', diff: 'EASY' },
        { label: '3 of 4 weeks', diff: 'MID' },
        { label: '4 clean weeks', diff: 'HARD' },
      ],
    },
    {
      tier: TIER_LABELS[3],
      opts: [
        { label: 'Bank the month', diff: 'EASY' },
        { label: 'Month + streak', diff: 'MID' },
        { label: 'Level-up gate', diff: 'HARD' },
      ],
    },
  ];
}
